import { useState } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import {
  Bell,
  FileText,
  Wallet,
  Key,
  Home,
  CreditCard,
  BarChart3,
  Store,
  Link,
  BookOpen,
  Users as UsersIcon,
  Menu,
  User,
  Contact,
  SlidersHorizontal,
  UserPlus,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { ROUTES } from '../../../config/routes'
import { useAuth } from '../../../lib/auth.context'
import { usePartner } from '../../../lib/hooks/usePartner'
import { useTheme } from '../../../lib/theme-context'
import { useIsMobile } from '../../../hooks/use-mobile'

type MenuItem = {
  title: string
  icon: LucideIcon
  href: string
  comingSoon?: boolean
}

const getMenuItems = (): MenuItem[] => [
  {
    title: 'Dashboard',
    icon: Home,
    href: ROUTES.PARTNER.DASHBOARD,
  },
  {
    title: 'Transactions',
    icon: CreditCard,
    href: ROUTES.PARTNER.TRANSACTIONS,
  },
  {
    title: 'Payers',
    icon: Contact,
    href: ROUTES.PARTNER.PAYERS,
  },
  {
    title: 'Payouts',
    icon: Wallet,
    href: ROUTES.PARTNER.PAYOUTS,
  },
  {
    title: 'Reports',
    icon: BarChart3,
    href: '#',
    comingSoon: true,
  },
  {
    title: 'Daily Reports',
    icon: FileText,
    href: ROUTES.PARTNER.DAILY_REPORTS,
  },
  {
    title: 'Merchants',
    icon: Store,
    href: ROUTES.ADMIN.MERCHANTS,
  },
  {
    title: 'Self-onboarding',
    icon: UserPlus,
    href: ROUTES.PARTNER.SELF_ONBOARDING,
  },
  {
    title: 'Fee Schedules',
    icon: SlidersHorizontal,
    href: ROUTES.PARTNER.FEE_SCHEDULES,
  },
  {
    title: 'API Keys',
    icon: Key,
    href: ROUTES.PARTNER.API_KEYS,
  },
  {
    title: 'Webhooks',
    icon: Link,
    href: ROUTES.PARTNER.WEBHOOKS,
  },
  {
    title: 'Notifications',
    icon: Bell,
    href: '#',
    comingSoon: true,
  },
  // {
  //   title: 'Integrations',
  //   icon: Puzzle,
  //   href: ROUTES.PARTNER.INTEGRATIONS,
  // },
  {
    title: 'Documentation',
    icon: BookOpen,
    href: ROUTES.PARTNER.DOCUMENTATION,
  },
  {
    title: 'Users',
    icon: UsersIcon,
    href: ROUTES.PARTNER.USERS,
  },
  {
    title: 'Partner Profile',
    icon: User,
    href: ROUTES.PARTNER.PROFILE,
  },
]

const lightLogo = '/fanfair.png'
const darkLogo = '/fanfairreversed.png'
const mobileLightLogo = '/fanfarmobile.png'
const mobileDarkLogo = '/fanfairreversedmobile.png'

interface SidebarProps {
  collapsed: boolean
  onToggle: () => void
}

// export function SidebarNav() {
export const SidebarNav = ({ collapsed, onToggle }: SidebarProps) => {
  const [imageError, setImageError] = useState(false)
  const { theme, setTheme } = useTheme()
  const location = useLocation()
  const { status } = useAuth()
  const { partnerId } = usePartner()
  const isAuthenticated = status === 'authenticated'
  const isMobile = useIsMobile()

  // Determine which logo to use based on current theme
  const currentTheme = theme === 'light' ? setTheme : theme
  const logoSrc = currentTheme === 'dark' ? darkLogo : lightLogo
  const mobileLogoSrc = currentTheme === 'dark' ? mobileDarkLogo : mobileLightLogo

  if (isMobile) {
    collapsed = true
  }

  // Log navigation state for debugging
  console.log('[SidebarNav] Current path:', location.pathname)
  console.log('[SidebarNav] isAuthenticated:', isAuthenticated)

  return (
    <div
      className={`fixed left-0 top-0 h-full flex flex-col bg-card border-r border-border transition-all duration-300 z-10 ${
        collapsed ? 'w-16' : 'w-sidebar'
      }`}
    >
      {/* Logo Section */}
      <div className="h-header shrink-0 flex items-center justify-between px-4 border-b border-border">
        {!collapsed && (
          <div className="flex items-center gap-2">
            {!imageError ? (
              <img
                src={logoSrc}
                alt="FanFair"
                className="h-8 w-auto"
                onError={(e) => {
                  console.error('Logo failed to load:', e.currentTarget.src)
                  setImageError(true)
                }}
                onLoad={() => console.log('Logo loaded successfully:', logoSrc)}
              />
            ) : (
              <div className="text-lg font-bold text-primary">FanFair</div>
            )}
          </div>
        )}
        {collapsed && (
          <div className="flex items-center justify-center w-8">
            {!isMobile ? (
              <div>
                {!imageError ? (
                  <img
                    src={logoSrc}
                    alt="FanFair"
                    className="h-6 w-6 object-contain"
                    onError={(e) => {
                      console.error('Logo failed to load:', e.currentTarget.src)
                      setImageError(true)
                    }}
                    onLoad={() => console.log('Logo loaded successfully:', logoSrc)}
                  />
                ) : (
                  <div className="text-sm font-bold text-primary">FF</div>
                )}
              </div>
            ) : (
              <div>
                {!imageError ? (
                  <img
                    src={mobileLogoSrc}
                    alt="FanFair"
                    className="h-6 w-6 object-contain"
                    onError={(e) => {
                      console.error('Logo failed to load:', e.currentTarget.src)
                      setImageError(true)
                    }}
                    onLoad={() => console.log('Logo loaded successfully:', logoSrc)}
                  />
                ) : (
                  <div className="text-sm font-bold text-primary">FF</div>
                )}
              </div>
            )}
          </div>
        )}
        <button
          onClick={onToggle}
          className="hidden md:block p-2 mt-1 hover:bg-accent rounded-lg transition-colors"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          <Menu className="h-5 w-5" />
        </button>
      </div>
      <nav className="flex-1 min-h-0 overflow-y-auto p-4">
        <div className="space-y-2">
          {getMenuItems().map((item) => {
            const Icon = item.icon
            const baseClasses = `fanfair-sidebar-item ${collapsed ? 'justify-center px-3' : 'justify-start'}`
            return item.comingSoon ? (
              <div
                key={item.title}
                aria-disabled="true"
                className={`${baseClasses} cursor-not-allowed opacity-60 text-muted-foreground`}
                onClick={(e) => e.preventDefault()}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && (
                  <div className="flex items-center gap-2">
                    <span>{item.title}</span>
                    <span className="text-xs rounded bg-muted px-2 py-0.5 text-muted-foreground">Coming soon</span>
                  </div>
                )}
              </div>
            ) : (
              <NavLink
                key={item.title}
                to={item.href}
                // end={item.href === '/'}
                className={({ isActive }) => `${baseClasses} ${isActive ? 'active' : ''}`}
              >
                <Icon className="h-5 w-5 flex-shrink-0" />
                {!collapsed && <span>{item.title}</span>}
              </NavLink>
            )
          })}
        </div>
      </nav>
    </div>
  )
}
